import { useRef, useState, type Dispatch, type SetStateAction } from 'react'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { useRouter } from '@tanstack/react-router'
import { App, Button, Empty, Modal, Tag } from 'antd'
import dayjs from 'dayjs'
import { fillRentalContract } from '@super-mega/server/contracts/order'
import { WorkspaceHeader } from '@/components/workspace-header.tsx'
import { refreshMoneyQueries } from '@/features/bill/index.ts'
import {
  CONTRACT_TEMPLATE_TYPES,
  ContractLexicalEditor,
  contractPrintHtml,
  printContractHtml,
  type ContractLexicalContent
} from '@/features/contract/index.ts'
import { useTRPC } from '@/trpc/react.ts'
import { cn } from '@/utils/index.ts'
import { OrderContractFields } from './order-contract-fields.tsx'
import type { ContractOrder, OrderContractDraft } from './order-contract-workspace.tsx'
import { RentalEnterpriseEditor } from './rental-enterprise-editor.tsx'

export function OrderContractEditor({
  orders,
  drafts,
  setDrafts,
  templates,
  orderType,
  documentId
}: {
  orders: ContractOrder[]
  drafts: OrderContractDraft[]
  setDrafts: Dispatch<SetStateAction<OrderContractDraft[]>>
  templates: { id: number; name: string; type: string }[]
  orderType: 'rental' | 'service'
  documentId?: number
}) {
  const router = useRouter()
  const trpc = useTRPC()
  const cache = useQueryClient()
  const { message, modal } = App.useApp()
  const [activeKey, setActiveKey] = useState<string | undefined>(
    () => drafts.find((draft) => draft.sourceDocumentId === documentId)?.key ?? drafts[0]?.key
  )
  const [supplementOrderId, setSupplementOrderId] = useState<number>()
  const [supplementOpen, setSupplementOpen] = useState(false)
  const [supplementBusy, setSupplementBusy] = useState(false)
  const [loading, setLoading] = useState(false)
  const [versions, setVersions] = useState<Record<number, Date>>(() =>
    Object.fromEntries(orders.map((order) => [order.id, order.updatedAt]))
  )
  const editor = useRef<HTMLDivElement>(null)
  const save = useMutation(trpc.order.contracts.save.mutationOptions())
  const active = drafts.find((draft) => draft.key === activeKey)
  const order = orders.find((item) => item.id === active?.orderId) ?? orders[0]
  const supplementOrder = orders.find((item) => item.id === supplementOrderId)?.rentalOrder
  const canSave = orders.some((item) => item.editable)
  const busy = save.isPending || loading || supplementBusy
  const update = (key: string, patch: Partial<OrderContractDraft>) =>
    setDrafts((current) => current.map((draft) => (draft.key === key ? { ...draft, ...patch, dirty: true } : draft)))
  const load = async (draft: OrderContractDraft, templateId: number) => {
    setLoading(true)
    return cache
      .fetchQuery({
        ...trpc.order.contracts.prepare.queryOptions({
          orderType,
          orderId: draft.orderId,
          templateId,
          partyName: draft.partyName
        }),
        staleTime: 0
      })
      .then((result) => {
        if (!dayjs(result.expectedUpdatedAt).isSame(versions[draft.orderId])) {
          throw new Error('订单已更新，请刷新页面后重新生成合同')
        }
        setVersions((current) => ({ ...current, [draft.orderId]: result.expectedUpdatedAt }))
        update(draft.key, {
          templateId,
          templateVersionId: result.templateVersionId,
          title: result.title,
          content: result.content,
          unresolved: result.unresolved,
          error: undefined,
          revision: draft.revision + 1
        })
      })
      .catch((error: unknown) => {
        void message.error(error instanceof Error ? error.message : '模板加载失败')
      })
      .finally(() => setLoading(false))
  }
  const requestLoad = (draft: OrderContractDraft, templateId: number) => {
    if (draft.content) {
      modal.confirm({
        title: '重新按模板填充？',
        content: '此合同的正文修改将被替换。',
        onOk: () => load(draft, templateId)
      })
    } else {
      void load(draft, templateId)
    }
  }
  const saveAll = async (print: boolean) => {
    const changed = drafts.filter((draft) => draft.dirty)
    if (changed.length && !canSave) {
      void message.warning('当前没有保存合同的权限，请刷新后查看已保存合同')
      return
    }
    for (const draft of changed) {
      if (
        Boolean(draft.error) ||
        !draft.content ||
        !draft.title.trim() ||
        (!draft.sourceDocumentId && (!draft.templateId || !draft.templateVersionId))
      ) {
        setActiveKey(draft.key)
        void message.warning(draft.error ?? `请完成「${draft.title}」的模板加载和合同名称`)
        return
      }
    }
    // 在用户点击时预留标签页，避免异步保存后被浏览器拦截。
    const printTab = print ? window.open('', '_blank') : null
    if (print && !printTab) {
      void message.warning('请允许浏览器打开新标签页后重试')
      return
    }
    if (printTab) printTab.opener = null
    setLoading(true)
    try {
      const result = changed.length
        ? await save.mutateAsync({
            orderType,
            items: changed.map((draft) => ({
              rentalOrderId: draft.orderId,
              sourceDocumentId: draft.sourceDocumentId,
              templateId: draft.templateId,
              templateVersionId: draft.templateVersionId,
              expectedUpdatedAt: versions[draft.orderId],
              partyName: draft.partyName,
              title: draft.title.trim(),
              content: fillRentalContract(draft.content!, {}).content
            }))
          })
        : []
      const nextVersions = { ...versions }
      const saved = drafts.map((draft) => {
        const index = changed.findIndex((item) => item.key === draft.key)
        const document = index < 0 ? undefined : result[index]
        if (!document) {
          return draft
        }
        nextVersions[draft.orderId] = document.expectedUpdatedAt
        return {
          ...draft,
          content: document.content as ContractLexicalContent,
          sourceDocumentId: document.id,
          dirty: false,
          revision: draft.revision + 1
        }
      })
      setVersions(nextVersions)
      setDrafts(saved)
      if (changed.length) {
        void message.success(`已保存 ${changed.length} 份合同`)
      }
      await Promise.all([
        refreshMoneyQueries(cache, trpc),
        cache.invalidateQueries(trpc.order.contracts.documents.queryFilter()),
        cache.invalidateQueries(trpc.contract.document.list.queryFilter())
      ])
      if (printTab && !printTab.closed) {
        printTab.location.replace(
          router.buildLocation({
            to: '/orders/contracts/print',
            search: {
              orderType,
              ids: orders.map((item) => item.id),
              documentIds: saved.filter((draft) => draft.content).map((draft) => draft.sourceDocumentId!)
            }
          }).href
        )
      }
    } catch (error) {
      printTab?.close()
      void message.error(error instanceof Error ? error.message : '保存失败，请重试')
    }
    setLoading(false)
  }
  return (
    <div className="bg-page flex h-dvh min-h-0 flex-col overflow-hidden">
      <WorkspaceHeader>
        <div>
          <h1 className="m-0 text-base font-semibold">{orders.length > 1 ? '批量打印合同' : '订单合同'}</h1>
          <span className="text-content-muted text-xs">
            {orders.length} 个订单 · {drafts.length} 份合同
          </span>
        </div>
        <div className="flex gap-2">
          {active?.sourceDocumentId && !active.dirty && !active.readOnly && order.editable && (
            <Button disabled={busy} onClick={() => update(active.key, {})}>
              编辑合同
            </Button>
          )}
          <Button
            disabled={busy || !active?.content || active.dirty}
            onClick={() => {
              if (editor.current) {
                printContractHtml(contractPrintHtml(editor.current))
              }
            }}
          >
            打印当前合同
          </Button>
          {canSave && (
            <Button
              disabled={busy || !drafts.some((draft) => draft.dirty)}
              loading={save.isPending}
              onClick={() => void saveAll(false)}
            >
              一键保存
            </Button>
          )}
          <Button
            type="primary"
            disabled={busy || !drafts.length}
            loading={save.isPending}
            onClick={() => void saveAll(true)}
          >
            {canSave ? '保存并打印全部' : '打印全部'}
          </Button>
        </div>
      </WorkspaceHeader>
      {supplementOrder && (
        <Modal
          open={supplementOpen}
          afterClose={() => setSupplementOrderId(undefined)}
          title={`服务对象及资料 · ${supplementOrder.orderNo}`}
          width="64rem"
          footer={null}
          styles={{ body: { height: 'calc(80vh - 5rem)', display: 'flex', overflow: 'hidden' } }}
          centered
          mask={{ closable: false }}
          closable={!supplementBusy}
          keyboard={!supplementBusy}
          onCancel={() => setSupplementOpen(false)}
        >
          <RentalEnterpriseEditor
            key={supplementOrder.id}
            order={supplementOrder}
            onBusyChange={setSupplementBusy}
            onSaved={(savedOrder) => {
              setVersions((current) => ({ ...current, [savedOrder.id]: savedOrder.updatedAt }))
              setDrafts((current) =>
                current.map((draft) => {
                  if (draft.orderId !== savedOrder.id || draft.sourceDocumentId) return draft
                  const partyName =
                    savedOrder.contractPartyName ||
                    savedOrder.companyName ||
                    savedOrder.identityName ||
                    savedOrder.customerName
                  return {
                    ...draft,
                    partyName,
                    dirty: true,
                    error: draft.content ? '订单资料已更新，请重新按模板填充后保存合同' : undefined
                  }
                })
              )
              setSupplementOpen(false)
              void message.info('本单资料已更新；已加载的合同请重新按模板填充，正文修改将由你确认后替换')
            }}
          />
        </Modal>
      )}
      <main className="grid min-h-0 flex-1 grid-rows-[auto_minmax(0,1fr)] gap-3 overflow-hidden p-3 lg:grid-cols-[18rem_minmax(0,1fr)] lg:grid-rows-1">
        <nav aria-label="订单合同列表" className="max-h-56 min-w-0 overflow-auto lg:max-h-none">
          <div className="flex flex-col gap-3">
            {orders.map((item, orderIndex) => {
              const orderDrafts = drafts.filter((draft) => draft.orderId === item.id)
              const isActiveOrder = active?.orderId === item.id
              return (
                <section
                  key={item.id}
                  aria-label={`订单 ${item.orderNo}`}
                  className={cn(
                    'bg-surface overflow-hidden rounded-xl border',
                    isActiveOrder ? 'border-accent/50' : 'border-border'
                  )}
                >
                  <div className="border-border bg-surface-muted border-b px-3 py-3">
                    <div className="text-content-muted mb-2 flex items-center justify-between gap-2 text-xs">
                      <span>订单 {orderIndex + 1}</span>
                      <span className="bg-surface text-content rounded-md px-2 py-0.5 font-medium">
                        {orderDrafts.length} 份合同
                      </span>
                    </div>
                    <h2 className="text-content m-0 text-sm font-semibold break-all">{item.orderNo}</h2>
                    <div className="mt-1 flex items-center justify-between gap-2">
                      <span className="text-content-muted min-w-0 text-xs break-words">{item.customerName}</span>
                      {item.rentalOrder && item.canSupplement && (
                        <Button
                          size="small"
                          className="h-auto! px-0! text-xs! font-normal!"
                          type="link"
                          disabled={busy || !item.canSupplement}
                          title={item.rentalOrder.missingContractData.join('、') || '服务对象资料齐全'}
                          onClick={() => {
                            setSupplementOrderId(item.id)
                            setSupplementOpen(true)
                          }}
                        >
                          {item.rentalOrder.missingContractData.length
                            ? `待补齐 ${item.rentalOrder.missingContractData.length} 项`
                            : '资料齐全'}
                        </Button>
                      )}
                    </div>
                  </div>
                  <div className="p-3">
                    <div className="border-border mb-3 flex flex-col gap-2 border-l pl-2">
                      {orderDrafts.map((draft, contractIndex) => {
                        const isActive = activeKey === draft.key
                        const titlePrefix = `${item.orderNo} · `
                        const displayTitle = draft.title.startsWith(titlePrefix)
                          ? draft.title.slice(titlePrefix.length)
                          : draft.title
                        return (
                          <button
                            key={draft.key}
                            type="button"
                            disabled={busy}
                            onClick={() => setActiveKey(draft.key)}
                            aria-current={isActive ? 'true' : undefined}
                            title={draft.title}
                            className={cn(
                              'focus-visible:outline-accent flex w-full cursor-pointer flex-col gap-2 rounded-lg border p-3 text-left transition-colors focus-visible:outline-2 focus-visible:outline-offset-2 disabled:cursor-not-allowed disabled:opacity-60',
                              isActive
                                ? 'border-accent/50 bg-accent/10 text-content'
                                : 'border-border bg-surface text-content hover:bg-surface-muted'
                            )}
                          >
                            <span className="flex items-center justify-between gap-2 text-xs">
                              <span className="text-content-muted">合同 {contractIndex + 1}</span>
                              {isActive && <span className="text-accent font-medium">当前合同</span>}
                            </span>
                            <span className="text-sm font-medium break-words">{displayTitle}</span>
                            <span className="text-content-muted text-xs">
                              {CONTRACT_TEMPLATE_TYPES[draft.contractType].label} · {draft.dirty ? '待保存' : '已保存'}
                            </span>
                          </button>
                        )
                      })}
                      {!orderDrafts.length && <span className="text-content-muted p-2 text-xs">暂无合同</span>}
                    </div>
                    {item.editable && (
                      <Button
                        type="dashed"
                        block
                        disabled={busy || orderDrafts.length >= 20}
                        onClick={() => {
                          const key = crypto.randomUUID()
                          setDrafts((current) => [
                            ...current,
                            {
                              key,
                              orderId: item.id,
                              contractType: orderType === 'rental' ? 'lease' : 'service',
                              partyName: item.partyName,
                              title: `${item.orderNo} · 合同 ${orderDrafts.length + 1}`,
                              content: null,
                              unresolved: [],
                              revision: 0,
                              dirty: true
                            }
                          ])
                          setActiveKey(key)
                        }}
                      >
                        添加合同
                      </Button>
                    )}
                  </div>
                </section>
              )
            })}
          </div>
        </nav>
        <section className="border-border bg-surface flex min-h-0 min-w-0 flex-col overflow-hidden rounded-xl border">
          <div className="border-border flex flex-wrap items-center justify-between gap-2 border-b px-4 py-3">
            <div className="text-sm font-semibold">
              {order.orderNo}
              <span className="text-content-muted ml-2 font-normal">{order.customerName}</span>
            </div>
            <Tag>{active?.dirty ? '待保存' : '已保存'}</Tag>
          </div>
          {active ? (
            <div className="grid min-h-0 flex-1 overflow-auto lg:grid-cols-[17rem_minmax(0,1fr)] lg:overflow-hidden">
              <OrderContractFields
                draft={active}
                templates={templates}
                orderType={orderType}
                disabled={busy || !order.editable || Boolean(active.sourceDocumentId && !active.dirty)}
                loading={loading}
                onChange={(patch) => {
                  if (patch.contractType && active.content) {
                    modal.confirm({
                      title: '更换合同类型？',
                      content: '此合同的模板与正文将清空。',
                      onOk: () => update(active.key, patch)
                    })
                  } else {
                    update(active.key, patch)
                  }
                }}
                onLoad={(id) => requestLoad(active, id)}
                onRemove={() => {
                  setDrafts((current) => current.filter((draft) => draft.key !== active.key))
                  setActiveKey(drafts.find((draft) => draft.key !== active.key)?.key)
                }}
              />
              <div className="flex min-h-96 min-w-0 flex-col overflow-hidden">
                <div className="border-border flex justify-between border-b px-4 py-2">
                  <strong className="text-sm">合同正文</strong>
                  <span className="text-content-muted text-xs">
                    {order.editable && (!active.sourceDocumentId || active.dirty) ? '可逐份修改' : '只读查看'}
                  </span>
                </div>
                <div ref={editor} className="flex min-h-0 flex-1 flex-col">
                  {active.content ? (
                    <ContractLexicalEditor
                      key={`${active.key}:${active.revision}:${busy || !order.editable || Boolean(active.sourceDocumentId && !active.dirty)}`}
                      initialValue={active.content}
                      readOnly={busy || !order.editable || Boolean(active.sourceDocumentId && !active.dirty)}
                      onChange={(content) =>
                        JSON.stringify(content) !== JSON.stringify(active.content) &&
                        update(active.key, { content, unresolved: fillRentalContract(content, {}).unresolved })
                      }
                    />
                  ) : (
                    <Empty className="m-auto" description="请选择合同模板并加载正文" />
                  )}
                </div>
              </div>
            </div>
          ) : (
            <Empty className="m-auto" description="请添加合同" />
          )}
        </section>
      </main>
    </div>
  )
}
